<template>
    <div v-if="isLoading" class="d-flex align-items-center" style="gap: 5px">
        <div class="spinner-border spinner-border-sm text-primary" role="status"></div>
        <span>{{ $t('components.datatable.index.loadingText') }}</span>
    </div>
    <template v-else>
        <div v-if="keepaHasData" class="keepa-canvas">
            <div class="d-flex justify-content-between align-items-center">
                <div class="keepa-title" style="font-size: 14px; font-weight: 500; line-height: 21px; margin-top: 20px; margin-bottom: 10px; color: #00203f">Fiyat Verileri</div>
                <button type="button" class="btn btn-sm btn-outline-secondary" @click="refreshKeepaData"><i class="bi-arrow-clockwise"></i> {{ $t('views.inventory.modals.productDetail.keepa.refresh') }}</button>
            </div>

            <Line
                :data="keepaChartData"
                :key="chartKey"
                :plugins="[
                    {
                        afterDraw: (chart) => {
                            if (chart.tooltip?._active?.length) {
                                let x = chart.tooltip._active[0].element.x
                                let yAxis = chart.scales.y1
                                let ctx = chart.ctx
                                ctx.save()
                                ctx.beginPath()
                                ctx.moveTo(x, yAxis.top)
                                ctx.lineTo(x, yAxis.bottom)
                                ctx.lineWidth = 1
                                ctx.strokeStyle = '#ffb53e'
                                ctx.stroke()
                                ctx.restore()
                            }
                        },
                    },
                ]"
                :options="options"
                style="min-width: 100%; max-height: 200px"
            />
        </div>
        <div v-else class="alert alert-soft-primary">{{ $t('components.productDetailModal.noKeepaPriceData') }}</div>

        <div v-if="keepaOfferHasData">
            <div class="keepa-title" style="font-size: 14px; font-weight: 500; line-height: 21px; margin-top: 20px; margin-bottom: 10px; color: #00203f">Teklif Verileri</div>
            <Line
                :data="keepaOffersChartData"
                :plugins="[
                    {
                        afterDraw: (chart) => {
                            if (chart.tooltip?._active?.length) {
                                let x = chart.tooltip._active[0].element.x
                                let yAxis = chart.scales.y3
                                let ctx = chart.ctx
                                ctx.save()
                                ctx.beginPath()
                                ctx.moveTo(x, yAxis.top)
                                ctx.lineTo(x, yAxis.bottom)
                                ctx.lineWidth = 1
                                ctx.strokeStyle = '#ffb53e'
                                ctx.stroke()
                                ctx.restore()
                            }
                        },
                    },
                ]"
                :options="offersOptions"
                style="min-width: 100%; max-height: 200px"
            />
        </div>
        <div v-else class="alert alert-soft-primary">{{ $t('components.productDetailModal.noKeepaOfferData') }}</div>
    </template>
</template>

<script>
import { mapActions, mapState, mapGetters } from 'vuex'
import moment from 'moment'
import { countryInfo } from '@/utils/helper'
import { formatCurrency } from '@/utils/format-number'
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend } from 'chart.js'
import { Line } from 'vue-chartjs'
import { subDays, formatISO } from 'date-fns'

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend)
const MINUTE_OFFSET = 21564000
const MINUTE_IN_MS = 60000
const countries = countryInfo()

export default {
    name: 'ProductDetailKeepaModal',
    components: {
        Line,
    },
    data() {
        const locale = this.$i18n.locale
        return {
            countries,
            moment,
            chartKey: 0,
            currentDateRange: 365,
            locale,
            keepaChartData: {
                labels: [],
                datasets: [
                    {
                        label: 'Sales Ranks',
                        backgroundColor: 'green',
                        data: [],
                        fill: false,
                        borderColor: 'green',
                        tension: 0,
                        pointRadius: 0,
                        borderWidth: 1,
                        yAxisID: 'y1',
                    },
                    {
                        label: 'AMZ',
                        backgroundColor: 'orange',
                        data: [],
                        fill: true,
                        borderColor: 'orange',
                        tension: 0.1,
                        pointRadius: 2,
                        borderWidth: 0,
                        yAxisID: 'y2',
                    },
                    {
                        label: 'FBA',
                        backgroundColor: 'rgb(255, 87, 34)',
                        data: [],
                        fill: false,
                        borderColor: 'rgb(255, 87, 34)',
                        tension: 0.1,
                        pointRadius: 2,
                        borderWidth: 0,
                        yAxisID: 'y2',
                    },
                    {
                        label: 'FBM',
                        backgroundColor: 'rgb(3, 155, 229)',
                        data: [],
                        fill: false,
                        borderColor: 'rgb(3, 155, 229)',
                        tension: 0.1,
                        pointRadius: 2,
                        borderWidth: 0,
                        yAxisID: 'y2',
                    },
                    {
                        label: 'Buybox',
                        backgroundColor: 'rgb(255, 0, 75)',
                        data: [],
                        fill: false,
                        borderColor: 'rgb(255, 0, 75)',
                        tension: 0.1,
                        pointRadius: 2,
                        borderWidth: 0,
                        yAxisID: 'y2',
                        pointStyle: 'rectangle',
                    },
                ],
            },
            keepaOffersChartData: {
                labels: [],
                datasets: [
                    {
                        label: 'Offers',
                        backgroundColor: 'purple',
                        data: [],
                        fill: false,
                        borderColor: 'purple',
                        tension: 0,
                        pointRadius: 0,
                        borderWidth: 1,
                        yAxisID: 'y3',
                    },
                    {
                        label: 'Reviews',
                        backgroundColor: 'green',
                        data: [],
                        fill: false,
                        borderColor: 'green',
                        tension: 0,
                        pointRadius: 0,
                        borderWidth: 1,
                        yAxisID: 'y4',
                    },
                ],
            },
            options: {
                responsive: true,
                maintainAspectRatio: true,
                plugins: {
                    tooltip: {
                        mode: 'index',
                        intersect: false,
                        callbacks: {
                            title: function (tooltipItem) {
                                return new Date(tooltipItem[0].label).toLocaleDateString(locale || 'tr-TR', {
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric',
                                })
                            },
                            label: function (tooltipItem) {
                                if (tooltipItem.dataset.yAxisID === 'y1') {
                                    return tooltipItem.dataset.label + ': ' + tooltipItem.formattedValue.toLocaleString()
                                }
                                const domainFromQuery = new URLSearchParams(window.location.search).get('domain')
                                const domain = domainFromQuery ? Object.keys(countryInfo()).find((key) => countryInfo()[key].domain === domainFromQuery) : 'US'
                                const currency = countryInfo()[domain].symbol

                                return tooltipItem.dataset.label + ': ' + formatCurrency(currency, +tooltipItem.raw / 100)
                            },
                        },
                    },
                    legend: {
                        display: true, // Efsaneyi göster
                        labels: {
                            font: {
                                size: 13, // Font boyutu
                            },
                            padding: 10, // Efsane etiketleri arasındaki dolgu
                            boxWidth: 20, // Efsane kutucuk genişliği
                            boxHeight: 5, // Efsane kutucuk yüksekliği
                            usePointStyle: true, // Efsane kutucuklarını nokta şeklinde kullan
                        },
                        onClick: function (e, legendItem, legend) {
                            const index = legendItem.datasetIndex
                            const ci = legend.chart
                            const meta = ci.getDatasetMeta(index)

                            // Meta verinin görünürlüğünü toggle et
                            meta.hidden = meta.hidden === null ? !ci.data.datasets[index].hidden : null

                            // Grafikteki değişiklikleri göstermek için güncelle
                            ci.update()
                        },
                    },
                },
                scales: {
                    x: {
                        display: false,
                        title: {
                            display: true,
                        },
                        ticks: {
                            padding: -10,
                        },
                    },
                    y1: {
                        type: 'linear',
                        display: true,
                        position: 'right',
                        min: 0,
                        title: {
                            display: true,
                        },
                        ticks: {
                            callback: function (value) {
                                return value.toLocaleString()
                            },
                            font: {
                                size: 9,
                            },
                        },
                        beginAtZero: true,
                        afterFit: function (scale) {
                            scale.width = 40
                        },
                    },
                    y2: {
                        type: 'linear',
                        display: true,
                        position: 'left',
                        min: 0,
                        grid: {
                            drawOnChartArea: false,
                        },
                        title: {
                            display: true,
                        },
                        ticks: {
                            callback: function (value) {
                                const domainFromQuery = new URLSearchParams(window.location.search).get('domain')
                                const domain = domainFromQuery ? Object.keys(countryInfo()).find((key) => countryInfo()[key].domain === domainFromQuery) : 'US'
                                const currency = countryInfo()[domain].symbol
                                return formatCurrency(currency, +value / 100)
                            },
                            font: {
                                size: 9,
                            },
                        },
                        afterFit: function (scale) {
                            scale.width = 40
                        },
                        beginAtZero: true,
                    },
                },
                hover: {
                    mode: 'index',
                    intersect: false,
                },
            },
            offersOptions: {
                responsive: true,
                maintainAspectRatio: true,
                plugins: {
                    layout: {
                        padding: {
                            left: 0,
                            right: 0,
                            top: 0,
                            bottom: 0,
                        },
                        width: 540,
                    },
                    tooltip: {
                        mode: 'index',
                        intersect: false,
                        callbacks: {
                            title: function (tooltipItem) {
                                return new Date(tooltipItem[0].label).toLocaleDateString(locale || 'tr-TR', {
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric',
                                })
                            },
                            label: function (tooltipItem) {
                                if (tooltipItem.dataset.yAxisID === 'y3') {
                                    return tooltipItem.dataset.label + ': ' + tooltipItem.formattedValue.toLocaleString()
                                }
                            },
                        },
                        axis: 'x',
                    },
                    legend: {
                        display: true,
                        labels: {
                            font: {
                                size: 13,
                            },
                            padding: 10,
                            boxWidth: 20,
                            boxHeight: 5,
                            usePointStyle: true,
                        },
                        onClick: function (e, legendItem, legend) {
                            const index = legendItem.datasetIndex
                            const ci = legend.chart
                            const meta = ci.getDatasetMeta(index)

                            // Meta verinin görünürlüğünü toggle et
                            meta.hidden = meta.hidden === null ? !ci.data.datasets[index].hidden : null

                            // Grafikteki değişiklikleri göstermek için güncelle
                            ci.update()
                        },
                    },
                },
                scales: {
                    x: {
                        display: false,
                        title: {
                            display: true,
                        },
                    },
                    y3: {
                        type: 'linear',
                        display: true,
                        position: 'left',
                        min: 0,
                        title: {
                            display: true,
                        },
                        ticks: {
                            callback: function (value) {
                                return value.toLocaleString()
                            },
                            font: {
                                size: 9,
                            },
                            stepSize: 5,
                        },
                        beginAtZero: true,
                        afterFit: function (scale) {
                            scale.width = 25
                        },
                    },
                    y4: {
                        type: 'linear',
                        display: true,
                        position: 'right',
                        min: 0,
                        title: {
                            display: true,
                        },
                        ticks: {
                            callback: function (value) {
                                return value.toLocaleString()
                            },
                            font: {
                                size: 9,
                            },
                        },
                        beginAtZero: true,
                        afterFit: function (scale) {
                            scale.width = 50
                        },
                    },
                },
                hover: {
                    mode: 'index',
                    intersect: false,
                },
            },
            formatCurrency,
            isLoading: true,
        }
    },
    computed: {
        ...mapState('inventory', ['keepa']),
        ...mapGetters('market', ['currentMarket']),
        getAmazonCountryURL() {
            return countries[this.currentMarket.short].domain
        },
        getCurrency() {
            return countries[this.currentMarket.short].symbol
        },
        keepaHasData() {
            // map datasets data is not null
            let _return = false
            this.keepaChartData.datasets.forEach((d) => {
                if (d.data.length > 0 && d.data.some((i) => !!i)) {
                    _return = true
                }
            })
            return _return
        },
        keepaOfferHasData() {
            let _return = false
            this.keepaOffersChartData.datasets.forEach((d) => {
                if (d.data.length > 0 && d.data.some((i) => !!i)) {
                    _return = true
                }
            })
            return _return
        },
    },
    created() {
        this.getInitKeepaData()
    },
    methods: {
        ...mapActions('inventory', ['getKeepa']),
        closeModal() {
            this.$emit('update:modelValue', false)
        },
        formatDate(date) {
            return moment(date).format('DD/MM/YYYY')
        },
        formatNumber(number) {
            if (number === null || number === undefined) return null

            const factor = Math.pow(10, 2)
            const rounded = Math.floor(number * factor + 1e-9) / factor
            return rounded.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            })
        },
        processChartData(data) {
            this.keepaChartData.labels = this.generateLast365Days()
            if (!data) return

            if (data.salesRankReference && data.salesRanks) {
                const dataMap = this.filterAndTransformDates(data.salesRanks, data.salesRankReference)
                let lastValidData
                this.keepaChartData.datasets[0].data = this.keepaChartData.labels.map((label) => {
                    const foundData = this.findDataForLabel(dataMap, label)
                    if (foundData !== undefined) {
                        lastValidData = foundData
                        return foundData
                    }
                    return lastValidData
                })
            }

            const dateTypes = [0, 10, 7, 18]

            dateTypes.forEach((type, idx) => {
                const dataMap = this.filterAndTransformDates(data.csv, type)
                this.keepaChartData.datasets[idx + 1].data = this.keepaChartData.labels.map((label) => {
                    return dataMap.get(label) || undefined
                })
            })
        },
        processOffersChartData(data) {
            this.keepaOffersChartData.labels = this.generateLast365Days()
            if (!data) return
            const dateTypes = [11, 17]
            if (data.csv.filter((i) => i).length > 0) {
                dateTypes.forEach((type, idx) => {
                    const dataMap = this.filterAndTransformDates(data.csv, type)
                    let lastValidData

                    this.keepaOffersChartData.datasets[idx].data = this.keepaOffersChartData.labels.map((label) => {
                        const foundData = this.findDataForLabel(dataMap, label)
                        if (foundData !== undefined) {
                            lastValidData = foundData
                            return foundData
                        }
                        return lastValidData
                    })
                })
            }
        },
        generateLast365Days() {
            const today = new Date()
            const days = this.currentDateRange
            return Array.from({ length: days }, (_, i) => formatISO(subDays(today, i), { representation: 'date' })).reverse()
        },
        filterAndTransformDates(data, offsetIndex) {
            const dataMap = new Map()
            if (data && data[offsetIndex]) {
                for (let i = 0; i < data[offsetIndex].length; i += 2) {
                    const item = data[offsetIndex][i]
                    if (item) {
                        const date = new Date((item + MINUTE_OFFSET) * MINUTE_IN_MS)
                        const dateStr = date.toISOString().split('T')[0]
                        if (data[offsetIndex][i + 1] > 0) {
                            dataMap.set(dateStr, data[offsetIndex][i + 1])
                        }
                    }
                }
            }
            return dataMap
        },
        findDataForLabel(dataMap, label) {
            const dateString = new Date(label).toISOString().split('T')[0]
            return dataMap.get(dateString) || undefined
        },
        getInitKeepaData(params = null) {
            this.getKeepa(params)
                .then(({ data }) => {
                    const keepaData = this.keepa
                    this.processChartData(keepaData)
                    this.processOffersChartData(keepaData)
                    this.chartKey += 1

                    if (data.error === 'not_supported') {
                        this.$toast.error(this.$t('views.inventory.modals.productDetail.keepa.notSupported'))
                    }
                })
                .finally(() => {
                    this.isLoading = false
                })
        },
        refreshKeepaData() {
            if (confirm(this.$t('views.inventory.modals.productDetail.keepa.confirmTab'))) {
                this.getInitKeepaData({ params: { refresh: true } })
            }
        },
    },
    watch: {},
}
</script>

<style lang="scss">
.dp__theme_light {
    --dp-primary-color: #ffb53e;
}
.custom-form-control {
    max-height: 37px;
    font-size: 12px;
}
[id^='ProductDetailOffersModal-'] {
    .modal-body {
        padding-top: 32px !important;
    }
    .close-button {
        width: 35px;
        height: 35px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 50%;

        &:hover {
            background-color: #f8fafd;
        }
    }
    .modal-table {
        margin-bottom: 0 !important;
        border-radius: 5px;

        &.specs {
            td {
                width: 269px;
                height: 45px;

                &:nth-child(odd) {
                    border-right: 0 !important;
                }
            }
        }

        tbody {
            tr {
                td {
                    font-size: 12px;
                    font-weight: 600;
                    line-height: 21px;
                    color: #677788;
                    height: 41px;
                }
                td:nth-child(odd) {
                    border-right: none;
                }
                td:nth-child(even) {
                    font-weight: 400;
                    color: #677788;
                    border-left: none;
                }
            }
        }

        .text-ellipsis {
            font-size: 11.9px;
            font-weight: 400;
            line-height: 17.9px;
            color: #677788;
            text-overflow: ellipsis;
            overflow: hidden;
            white-space: nowrap;

            &.title {
                max-width: 250px;
            }

            &.category {
                max-width: 125px;
            }
        }
        .dp__input_wrap {
            width: auto;
            > input {
                padding-inline-end: 12px !important;
                font-size: 14px;
                border: 0.6px solid #e7eaf3;
                color: #677788;
                width: 125px !important;
            }
        }
    }

    .nav-segment {
        margin-right: 2rem;

        .nav-link {
            font-weight: 500;
            font-size: 14px;
        }
    }

    .multiselect {
        width: 140px !important;
        min-width: auto !important;

        &__placeholder {
            display: block !important;
            margin-bottom: 0 !important;
            padding-top: 0 !important;
        }

        &__tags {
            padding: 12px 40px 0 8px !important;
        }

        &__select {
            line-height: normal !important;
        }
    }
}

.offers-table {
    td {
        height: 48px !important;
    }
}
</style>
